import React, { useState, useEffect, useRef } from "react";

/**
 * StaffModal — add a coordinator or caregiver to the agency.
 *
 * Adding a person and inviting them are ONE action here, not two. Splitting
 * them would leave half-created records whenever someone is interrupted, and a
 * staff list containing people who were never actually contacted.
 *
 * What this replaces: running setup_auth.py in a shell, choosing someone else's
 * password, and sending it to them. Now they set their own, which also means
 * there is nothing for an administrator to forget on their behalf.
 *
 * Deliberately separate from AccessPanel, which grants access to ONE CLIENT.
 * This grants a standing job in the agency. Keeping them apart is what stops a
 * coordinator inviting a grandmother from being one dropdown value away from
 * creating another coordinator.
 */

function fmtDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short", day: "numeric", year: "numeric",
  });
}

/**
 * Joins a list the way a person would say it: "Maris", "Maris and Daniel",
 * "Maris, Daniel and Priya". Used in the restore suggestion, which is a
 * sentence about people rather than a data dump.
 */
function speakList(names) {
  if (names.length <= 1) return names[0] || "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

export default function StaffModal({ agencyName, clients, prefill, onClose, onChanged }) {
  const [roles, setRoles]     = useState(null);
  const [pending, setPending] = useState(null);
  // `prefill` arrives when this was opened by Re-invite on a former team
  // member: their address, their name, and the job they used to hold, so an
  // administrator is not retyping what we already know about someone who
  // worked here.
  const [form, setForm]       = useState({
    email: prefill?.email || "",
    name:  prefill?.name  || "",
    role:  prefill?.role  || "caregiver",
  });
  // The restore suggestion is dismissed once acted on, in either direction.
  // It is an offer made once, not a banner that follows you down the form.
  const [suggestOpen, setSuggestOpen] = useState(
    Boolean(prefill?.lastClients?.length));
  // Optional per-client assignments chosen at hire time. Only used for caregiver
  // hires (coordinators are agency-wide). Ids of the clients the new caregiver
  // will be assigned to on accept.
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState(null);
  const [notice, setNotice]   = useState(null);
  const [copied, setCopied]   = useState(null);
  const [justAdded, setJustAdded] = useState(null);
  const newRef = useRef(null);

  async function load() {
    try {
      const [r, p] = await Promise.all([
        fetch("/api/staff/roles"),
        fetch("/api/staff/invitations"),
      ]);
      if (r.ok) {
        const d = await r.json();
        setRoles(d.roles);
        if (d.roles?.length) {
          setForm(f => (d.roles.some(x => x.value === f.role) ? f : { ...f, role: d.roles[0].value }));
        }
      }
      if (p.ok) setPending(await p.json());
    } catch {
      setError("We couldn't load this just now.");
    }
  }

  useEffect(() => { load(); }, []);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function invite() {
    setBusy(true); setError(null); setNotice(null);
    try {
      const r = await fetch("/api/staff/invitations", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: form.email.trim(),
          name:  form.name.trim() || null,
          role:  form.role,
          // Only meaningful for caregiver hires; the backend ignores it for
          // coordinators, but we only send it when relevant to keep intent clear.
          client_ids: form.role === "caregiver" ? selectedClientIds : [],
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(typeof d.error === "string" ? d.error : "Couldn't send that invitation.");

      if (d.already_member) {
        setNotice(d.message);
      } else {
        setNotice(`Invitation ready for ${d.email}. Copy the link below and send it to them.`);
        setJustAdded(d.id);
        setTimeout(() => newRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 60);
        setTimeout(() => setJustAdded(null), 6000);
      }
      setForm(f => ({ ...f, email: "", name: "" }));
      setSelectedClientIds([]);
      await load();
      onChanged?.();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function withdraw(inv) {
    if (!window.confirm(
      `Withdraw the invitation to ${inv.email}? The link stops working.`)) return;
    setError(null); setNotice(null);
    try {
      const r = await fetch(`/api/staff/invitations/${inv.id}`, { method: "DELETE" });
      const d = await r.json();
      if (!r.ok) throw new Error(typeof d.error === "string" ? d.error : "Couldn't withdraw it.");
      setNotice(`The invitation to ${inv.email} has been withdrawn.`);
      await load();
      onChanged?.();
    } catch (e) { setError(e.message); }
  }

  function copy(inv) {
    navigator.clipboard?.writeText(`${window.location.origin}/invite/${inv.token}`);
    setCopied(inv.id);
    setTimeout(() => setCopied(null), 2000);
  }

  const waiting = (pending || []).filter(i => i.status === "pending");
  const past    = (pending || []).filter(i => i.status !== "pending");
  const roleLabel = v => (roles || []).find(r => r.value === v)?.label || v;

  return (
    <div style={m.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={m.box}>
        <div style={m.header}>
          <span style={m.title}>Add someone to {agencyName || "the team"}</span>
          <button style={m.close} onClick={onClose} aria-label="Close">×</button>
        </div>

        <div style={m.scroll}>
          <p style={m.explain}>
            They'll get a link to set their own password. Nothing is emailed for
            you — you'll get the link to send however you normally reach them.
          </p>

          {/* Role first: it changes what the person can do, so it's the real choice */}
          <div style={m.choiceRow}>
            {(roles || []).map(r => (
              <button key={r.value} disabled={busy}
                      onClick={() => set("role", r.value)}
                      style={{ ...m.choice, ...(form.role === r.value ? m.choiceOn : {}) }}>
                <span style={m.choiceTitle}>{r.label}</span>
                <span style={m.choiceHint}>{r.description}</span>
              </button>
            ))}
          </div>

          <div style={m.formRow}>
            <input style={{ ...m.input, flex: 1 }} disabled={busy}
                   placeholder="Their email"
                   value={form.email} onChange={e => set("email", e.target.value)} />
            <input style={{ ...m.input, flex: 1 }} disabled={busy}
                   placeholder="Their name (optional)"
                   value={form.name} onChange={e => set("name", e.target.value)} />
          </div>

          {/* The restore suggestion, shown only on a re-invite and only where
              there is a picker to tick — a returning coordinator has no
              per-client assignments to restore, because their access is
              agency-wide and never came from assignments.

              A SUGGESTION, NEVER AN AUTOMATIC RESTORE. Someone returning after
              eight months should not silently regain a caseload that has moved
              on without them; those clients may have new people, or may no
              longer be with the agency at all. So this ticks boxes in a picker
              an administrator then confirms, and it does not submit anything.

              The spec offered three buttons — Restore all, Choose, Start fresh
              — but the last two are the same outcome: an empty picker. Two
              buttons for one result is a choice that isn't one, so they are
              collapsed here. */}
          {suggestOpen && form.role === "caregiver" && (
            <div style={m.restoreBlock}>
              <p style={m.restoreText}>
                {prefill.name || "They"} previously supported{" "}
                <strong>{speakList(prefill.lastClients.map(c => c.display_name))}</strong>.
              </p>
              <div style={m.restoreActions}>
                <button type="button" disabled={busy} style={m.restorePrimary}
                        onClick={() => {
                          setSelectedClientIds(prefill.lastClients.map(c => c.id));
                          setSuggestOpen(false);
                        }}>
                  {prefill.lastClients.length === 1
                    ? `Restore ${prefill.lastClients[0].display_name}`
                    : `Restore all ${prefill.lastClients.length}`}
                </button>
                <button type="button" disabled={busy} style={m.restoreSecondary}
                        onClick={() => { setSelectedClientIds([]); setSuggestOpen(false); }}>
                  Choose myself
                </button>
              </div>
            </div>
          )}

          {/* Optional per-client assignment — caregiver hires only. Coordinators
              are agency-wide, so no picker for them. */}
          {form.role === "caregiver" && (clients || []).length > 0 && (
            <div style={m.assignBlock}>
              <div style={m.assignLabel}>
                Assign to clients now <span style={m.assignOptional}>(optional)</span>
              </div>
              <div style={m.assignHint}>
                They'll see these clients as soon as they accept. You can also assign
                them later from a client's page.
              </div>
              <div style={m.assignList}>
                {(clients || []).map(c => {
                  const on = selectedClientIds.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      disabled={busy}
                      onClick={() => setSelectedClientIds(ids =>
                        on ? ids.filter(x => x !== c.id) : [...ids, c.id])}
                      style={{ ...m.assignChip, ...(on ? m.assignChipOn : {}) }}
                    >
                      <span style={m.assignCheck}>{on ? "✓" : ""}</span>
                      {c.display_name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <button style={{ ...m.primaryBtn, ...(busy ? m.disabled : {}) }}
                  onClick={invite} disabled={busy || !form.email.trim()}>
            {busy ? "Creating…" : "Create invitation"}
          </button>

          {error  && <div style={m.error}>{error}</div>}
          {notice && <div style={m.notice}>{notice}</div>}

          {waiting.length > 0 && (
            <>
              <div style={m.sectionLabel}>Waiting to accept</div>
              {waiting.map(i => (
                <div key={i.id}
                     ref={i.id === justAdded ? newRef : null}
                     style={{ ...m.row, ...(i.id === justAdded ? m.rowNew : {}) }}>
                  <div style={m.rowMain}>
                    <div style={m.rowLabel}>{i.name || i.email}</div>
                    <div style={m.rowMeta}>
                      {roleLabel(i.role)}
                      {i.expires_at ? ` · link expires ${fmtDate(i.expires_at)}` : ""}
                    </div>
                  </div>
                  <div style={m.rowActions}>
                    <button style={m.ghostBtn} onClick={() => copy(i)}>
                      {copied === i.id ? "Copied ✓" : "Copy link"}
                    </button>
                    {/* The mistyped-email case: without this a link sent to the
                        wrong address stays live for a fortnight. */}
                    <button style={m.dangerBtn} onClick={() => withdraw(i)}>
                      Withdraw
                    </button>
                  </div>
                </div>
              ))}
            </>
          )}

          {past.length > 0 && (
            <>
              <div style={m.sectionLabel}>No longer active</div>
              {past.map(i => (
                <div key={i.id} style={{ ...m.row, opacity: 0.55 }}>
                  <div style={m.rowMain}>
                    <div style={m.rowLabel}>{i.name || i.email}</div>
                    <div style={m.rowMeta}>
                      {i.status === "accepted"
                        ? "Accepted — they're on the team now"
                        : i.status === "revoked" ? "Withdrawn" : "Expired"}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        <div style={m.footer}>
          <button style={m.cancelBtn} onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

const m = {
  overlay:     { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: 24 },
  box:         { background: "var(--color-surface)", borderRadius: "var(--radius-xl)", padding: "var(--space-8)", width: 620, maxWidth: "100%", maxHeight: "88vh", display: "flex", flexDirection: "column", gap: "var(--space-3)", overflow: "hidden" },
  header:      { display: "flex", alignItems: "center", justifyContent: "space-between" },
  title:       { fontSize: "var(--text-lg)", fontWeight: "var(--weight-semibold)", color: "var(--color-text-primary)" },
  close:       { background: "none", border: "none", fontSize: 22, color: "var(--color-text-tertiary)", cursor: "pointer", lineHeight: 1, padding: 0 },
  scroll:      { overflowY: "auto", minHeight: 0, flex: 1, display: "flex", flexDirection: "column", gap: "var(--space-2)" },
  explain:     { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", margin: 0, lineHeight: "var(--leading-normal)" },
  sectionLabel:{ fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", textTransform: "uppercase", letterSpacing: "0.06em", marginTop: "var(--space-5)" },
  choiceRow:   { display: "flex", gap: "var(--space-2)", flexWrap: "wrap" },
  choice:      { flex: "1 1 220px", textAlign: "left", background: "var(--color-bg-base)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-3)", cursor: "pointer", font: "inherit", display: "flex", flexDirection: "column", gap: 4 },
  choiceOn:    { borderColor: "var(--color-accent)", background: "var(--color-accent-subtle)" },
  choiceTitle: { fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", color: "var(--color-text-primary)" },
  choiceHint:  { fontSize: 11, color: "var(--color-text-tertiary)", lineHeight: 1.45 },
  formRow:     { display: "flex", gap: "var(--space-2)", flexWrap: "wrap" },
  input:       { fontSize: "var(--text-sm)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: "var(--space-2) var(--space-3)", color: "var(--color-text-primary)", background: "var(--color-bg-base)", fontFamily: "inherit", minWidth: 0, boxSizing: "border-box" },
  primaryBtn:  { fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", background: "var(--color-accent)", color: "#fff", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-5)", cursor: "pointer", font: "inherit", alignSelf: "flex-start" },
  // Tinted rather than bordered: this is an offer, not a warning. It sits
  // directly above the picker it fills in, so the cause and effect are one
  // glance apart.
  restoreBlock:     { display: "flex", flexDirection: "column", gap: "var(--space-3)", padding: "var(--space-4)", borderRadius: "var(--radius-md)", background: "var(--color-accent-subtle)" },
  restoreText:      { margin: 0, fontSize: "var(--text-sm)", color: "var(--color-text-primary)", lineHeight: "var(--leading-normal)" },
  restoreActions:   { display: "flex", gap: "var(--space-2)", flexWrap: "wrap" },
  restorePrimary:   { font: "inherit", fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", background: "var(--color-accent)", color: "var(--color-text-on-accent)", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-4)", cursor: "pointer" },
  restoreSecondary: { font: "inherit", fontSize: "var(--text-sm)", background: "none", color: "var(--color-text-secondary)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-4)", cursor: "pointer" },

  assignBlock:    { display: "flex", flexDirection: "column", gap: "var(--space-2)", marginTop: "var(--space-1)" },
  assignLabel:    { fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", color: "var(--color-text-secondary)" },
  assignOptional: { fontWeight: "var(--weight-regular)", color: "var(--color-text-tertiary)" },
  assignHint:     { fontSize: 11, color: "var(--color-text-tertiary)", lineHeight: 1.45 },
  assignList:     { display: "flex", flexWrap: "wrap", gap: "var(--space-2)" },
  assignChip:     { display: "inline-flex", alignItems: "center", gap: "var(--space-1)", fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-full)", padding: "var(--space-1) var(--space-3)", cursor: "pointer", font: "inherit" },
  assignChipOn:   { borderColor: "var(--color-accent)", background: "var(--color-accent-subtle)", color: "var(--color-text-primary)" },
  assignCheck:    { width: 12, display: "inline-block", color: "var(--color-accent)", fontWeight: "var(--weight-bold)" },
  disabled:    { opacity: 0.5, cursor: "not-allowed" },
  row:         { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "var(--space-3)", padding: "var(--space-3)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", background: "var(--color-bg-base)", transition: "background 400ms ease, border-color 400ms ease" },
  rowNew:      { background: "var(--color-accent-subtle)", borderColor: "var(--color-accent)" },
  rowMain:     { minWidth: 0 },
  rowLabel:    { fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", color: "var(--color-text-primary)" },
  rowMeta:     { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", marginTop: 2 },
  rowActions:  { display: "flex", gap: "var(--space-2)", alignItems: "center", flexShrink: 0 },
  dangerBtn:   { fontSize: "var(--text-xs)", background: "none", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: "6px 10px", color: "var(--color-critical)", cursor: "pointer", font: "inherit", whiteSpace: "nowrap" },
  ghostBtn:    { fontSize: "var(--text-xs)", background: "none", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: "6px 10px", color: "var(--color-text-secondary)", cursor: "pointer", font: "inherit", whiteSpace: "nowrap" },
  error:       { fontSize: "var(--text-sm)", color: "var(--color-critical)", background: "var(--color-critical-bg)", borderRadius: "var(--radius-sm)", padding: "var(--space-2) var(--space-3)" },
  notice:      { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", background: "var(--color-accent-subtle)", border: "1px solid var(--color-accent)", borderRadius: "var(--radius-sm)", padding: "var(--space-2) var(--space-3)" },
  footer:      { display: "flex", justifyContent: "flex-end", paddingTop: "var(--space-2)" },
  cancelBtn:   { fontSize: "var(--text-sm)", background: "none", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-4)", color: "var(--color-text-secondary)", cursor: "pointer", font: "inherit" },
};
